import React from 'react';
import { LinkedInEnrichmentData } from '../../linkedin_enrichment/types';

interface Props {
  linkedInData: LinkedInEnrichmentData | null;
  isLoading: boolean;
  accentColor?: string;
}

const fieldRow = (label: string, value: any) => {
  if (value === null || value === undefined || value === '') return null;
  return (
    <div key={label} className="flex justify-between py-2.5 border-b border-gray-100 last:border-0">
      <span className="text-sm text-gray-500">{label}</span>
      <span className="text-sm font-medium text-gray-900 text-right max-w-[60%]">{String(value)}</span>
    </div>
  );
};

export const LinkedInProfileDisplay: React.FC<Props> = ({ linkedInData, isLoading, accentColor = '#3B82F6' }) => {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-4 border-t-transparent" style={{ borderColor: accentColor, borderTopColor: 'transparent' }} />
      </div>
    );
  }

  if (!linkedInData?.extracted_data) {
    return (
      <div className="text-center py-12">
        <svg className="w-12 h-12 text-gray-300 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <p className="text-sm text-gray-500 mb-1">No LinkedIn data available</p>
        <p className="text-xs text-gray-400">Run a LinkedIn enrichment to populate this section.</p>
      </div>
    );
  }

  const d = linkedInData.extracted_data;
  const specialties = d.specialties || d.specialities || [];
  const specialtiesList: any[] = Array.isArray(specialties)
    ? specialties
    : typeof specialties === 'string'
    ? specialties.split(',').map((s: string) => s.trim())
    : [];
  const leadership: any[] = d.leadership || [];
  const jobOpenings: any[] = d.job_openings || d.jobs || [];
  const locations: any[] = d.locations || [];
  const affiliates: any[] = d.affiliated_companies || [];

  return (
    <div className="space-y-6">
      {/* Company Profile */}
      <div>
        <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Company Profile</h4>
        <div className="bg-gray-50 rounded-lg px-4">
          {fieldRow('Company Name', d.company_name)}
          {fieldRow('Tagline', d.tagline)}
          {fieldRow('Industry', d.industry)}
          {fieldRow('Company Type', d.company_type)}
          {fieldRow('Founded', d.founded_year)}
          {fieldRow('Company Size', d.company_size)}
          {fieldRow('Headquarters', d.headquarters)}
          {fieldRow('Website', d.website)}
          {fieldRow('Phone', d.phone)}
        </div>
      </div>

      {/* Description */}
      {(d.about || d.description) && (
        <div>
          <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">About</h4>
          <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">{d.about || d.description}</p>
        </div>
      )}

      {/* Social Metrics */}
      {(d.follower_count != null || d.employee_count_on_linkedin != null || jobOpenings.length > 0) && (
        <div>
          <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Social Metrics</h4>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {d.follower_count != null && (
              <div className="bg-blue-50 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-blue-700">{Number(d.follower_count).toLocaleString()}</p>
                <p className="text-xs text-blue-600 mt-1">Followers</p>
              </div>
            )}
            {d.employee_count_on_linkedin != null && (
              <div className="bg-green-50 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-green-700">{Number(d.employee_count_on_linkedin).toLocaleString()}</p>
                <p className="text-xs text-green-600 mt-1">Employees on LinkedIn</p>
              </div>
            )}
            {jobOpenings.length > 0 && (
              <div className="bg-purple-50 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-purple-700">{jobOpenings.length}</p>
                <p className="text-xs text-purple-600 mt-1">Open Positions</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Specialties */}
      {specialtiesList.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Specialties</h4>
          <div className="flex flex-wrap gap-2">
            {specialtiesList.map((s: any, i: number) => (
              <span key={i} className="px-3 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700">
                {typeof s === 'object' ? s.name || JSON.stringify(s) : String(s)}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Leadership */}
      {leadership.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Leadership</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {leadership.map((l: any, i: number) => (
              <div key={i} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <div className="w-9 h-9 rounded-full bg-gray-200 flex items-center justify-center text-gray-500 text-sm font-semibold flex-shrink-0">
                  {(typeof l === 'object' ? l.name || '?' : String(l))[0].toUpperCase()}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{typeof l === 'object' ? l.name || 'Unknown' : String(l)}</p>
                  {typeof l === 'object' && l.title && <p className="text-xs text-gray-500 truncate">{l.title}</p>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Locations */}
      {locations.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Locations</h4>
          <div className="flex flex-wrap gap-2">
            {locations.map((loc: any, i: number) => (
              <span key={i} className="px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                {typeof loc === 'object' ? loc.text || loc.name || JSON.stringify(loc) : String(loc)}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Affiliated Companies */}
      {affiliates.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Affiliated Companies</h4>
          <div className="flex flex-wrap gap-2">
            {affiliates.map((name: any, i: number) => (
              <span key={i} className="px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">{String(name)}</span>
            ))}
          </div>
        </div>
      )}

      {/* Department Breakdown */}
      {d.headcount_by_department && Object.keys(d.headcount_by_department).length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Team by Department</h4>
          <div className="space-y-2">
            {Object.entries(d.headcount_by_department)
              .sort(([, a]: any, [, b]: any) => b - a)
              .map(([dept, count]: [string, any]) => {
                const total = Object.values(d.headcount_by_department).reduce((s: number, n: any) => s + n, 0);
                const pct = Math.round((count / total) * 100);
                return (
                  <div key={dept} className="flex items-center gap-3">
                    <span className="text-xs text-gray-600 w-40 truncate">{dept}</span>
                    <div className="flex-1 bg-gray-100 rounded-full h-2">
                      <div className="h-2 rounded-full bg-blue-500" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="text-xs font-medium text-gray-700 w-8 text-right">{count}</span>
                  </div>
                );
              })}
          </div>
        </div>
      )}

      {/* People Insights */}
      {d.additional_info && (() => {
        const insights: { title: string; data: Record<string, number> }[] = [];
        const mapping: Record<string, string> = {
          people_locations: 'Employee Locations',
          people_skill_explicit: 'Top Skills',
          people_school: 'Top Schools',
          people_field_of_study: 'Fields of Study',
        };
        for (const [key, label] of Object.entries(mapping)) {
          const val = d.additional_info?.[key];
          if (val && typeof val === 'object' && Object.keys(val).length > 0) {
            insights.push({ title: label, data: val });
          }
        }
        if (insights.length === 0) return null;
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {insights.map(({ title, data }) => (
              <div key={title}>
                <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">{title}</h4>
                <div className="space-y-1.5">
                  {Object.entries(data).slice(0, 8).map(([name, count]) => (
                    <div key={name} className="flex justify-between text-xs">
                      <span className="text-gray-600 truncate mr-2">{name}</span>
                      <span className="font-medium text-gray-800 flex-shrink-0">{count}</span>
                    </div>
                  ))}
                  {Object.keys(data).length > 8 && <p className="text-xs text-gray-400">+{Object.keys(data).length - 8} more</p>}
                </div>
              </div>
            ))}
          </div>
        );
      })()}

      {/* Funding */}
      {d.funding_info && (
        <div>
          <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Funding</h4>
          <div className="bg-gray-50 rounded-lg px-4">
            {fieldRow('Last Round', d.funding_info.last_round_type)}
            {fieldRow('Amount Raised', d.funding_info.last_round_raised)}
            {fieldRow('Total Funding', d.funding_info.total_funding)}
            {fieldRow('Rounds', d.funding_info.rounds)}
          </div>
        </div>
      )}

      {/* Metadata */}
      <div className="pt-4 border-t border-gray-200">
        <div className="flex items-center justify-between text-xs text-gray-400">
          <span>
            {linkedInData.fields_extracted}/{linkedInData.total_possible_fields} fields extracted
            {linkedInData.completion_percentage != null && ` (${linkedInData.completion_percentage}% complete)`}
          </span>
          {linkedInData.extraction_completed_at && (
            <span>Last updated: {new Date(linkedInData.extraction_completed_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
          )}
        </div>
      </div>
    </div>
  );
};
